// PaintedWorldCoordinator — Colyseus-owning gameplay scene for the painted world (SMA-65).
//
// Replaces OverworldScene as the single Colyseus room owner when the
// painted-world feature flag is on. Supersedes PaintedOverworldShell (SMA-42)
// by adding ZoneMessageBus integration, system-level message handlers, and
// coordinate normalization.
//
// Architecture:
//   - Owns the Colyseus connection to OverworldRoom (no new room)
//   - Creates and owns a ZoneMessageBus instance
//   - Forwards zone-relevant server messages → ZoneMessageBus
//   - Handles system-level messages directly (portals, hearth chat, etc.)
//   - Passes the bus to each ConnectedPaintedScene via init data
//   - Listens for local position events from ConnectedPaintedScene
//   - Drives SceneRouter for zone navigation with watercolor transitions

import * as Phaser from 'phaser';
import { Client, Room } from 'colyseus.js';
import type { OverworldRoomState } from '@game/server/schemas';
import {
  MSG_ZONE_ENTER,
  MSG_ZONE_LEAVE,
  MSG_ZONE_TRANSITION_REQUEST,
  MSG_LOCAL_POSITION_UPDATE,
  MSG_ZONE_GRAPH,
  MSG_PLAYER_POSITION,
  MSG_ENTITY_SPAWN,
  MSG_ENTITY_DESPAWN,
  MSG_NPC_SPAWN,
  MSG_NPC_DIALOGUE,
  MSG_GATHER_NODE_SPAWN,
  MSG_GATHER_NODE_COOLDOWN,
  MSG_GATHER_COMPLETE,
  MSG_GATHER_CANCELLED,
  MSG_ENEMY_WINDUP,
  MSG_PLAYER_FAINT,
  MSG_WEATHER_CHANGE,
  MSG_SKY_STATE,
  MSG_PLAYER_REALM_CHANGE,
  MSG_REALM_FIRST_VISIT,
  MSG_FIRE_SAT,
  MSG_FIRE_STOOD,
  MSG_MARSHMALLOW_ROAST_TICK,
  MSG_MARSHMALLOW_ROAST_COMPLETE,
  MSG_FIRE_EMOTE_PLAYED,
  MSG_DUNGEON_GATE_ENTER,
  MSG_HOME_ZONE_ENTER,
  MSG_TOWN_ZONE_ENTER,
  MSG_TOWN_SCREEN_CHANGED,
  MSG_HEARTH_WHISPER_BROADCAST,
  MSG_RETURN_DIGEST,
  MSG_PLAYER_ATTACK_HIT,
  MSG_EXPEDITION_STATE,
  MSG_EXPEDITION_REWARD_GRANTED,
  MSG_PET_SNAPSHOT,
  MSG_CRANE_CROSSING,
  MSG_CRANE_DEPARTED,
  MSG_WAYSTONE_INFO,
  MSG_AUTO_TRAVEL_STATE,
  MSG_AUTO_TRAVEL_DESTINATIONS,
  MSG_JOURNAL_SNAPSHOT,
  MSG_JOURNAL_WHISPER_GRANTED,
  type WhisperGrantedPayload,
  COMBAT_PROTOCOL_VERSION,
  type ZoneEnterEvent,
  type ZoneLeaveEvent,
  type ZoneTransitionRequest,
  type LocalPositionUpdateEvent,
  type ZoneGraphPayload,
  type PlayerPositionEvent,
  type EntitySpawnEvent,
  type EntityDespawnEvent,
  type NpcSpawnEvent,
  type NpcDialogueEvent,
  type GatherNodeSpawnEvent,
  type GatherNodeCooldownEvent,
  type GatherCompleteResult,
  type GatherCancelledEvent,
  type EnemyWindupEvent,
  type PlayerFaintEvent,
  type WeatherChangePayload,
  type SkyStatePayload,
  type PlayerRealmChangeEvent,
  type RealmFirstVisitEvent,
  type FireSatEvent,
  type FireStoodEvent,
  type MarshmallowRoastTickEvent,
  type MarshmallowRoastCompleteEvent,
  type FireEmotePlayedEvent,
  type HomeZoneEnterEvent,
  type TownZoneEnterEvent,
  type TownScreenChangedEvent,
  type HearthWhisperBroadcastEvent,
  type ReturnDigestPayload,
  type PlayerAttackHitEvent,
  type ExpeditionStateBroadcast,
  type ExpeditionRewardGrantedEvent,
  type PetSnapshotEvent,
  type CraneCrossingEvent,
  type CraneDepartedEvent,
  type WaystoneInfoPayload,
  type AutoTravelStateEvent,
  type AutoTravelDestinationsEvent,
} from '@game/shared';
import { COLYSEUS_URL } from '../config/network';
import { getDemoUserJoinOptions } from '../config/demoUsers';
import { FAINT_OVERLAY_SCENE_KEY, JOURNAL_SCENE_KEY } from './sceneKeys';
import {
  PaintedScene,
  PAINTED_SCENE_EVENTS,
  PAINTED_SHELL_MANAGED_REGISTRY_KEY,
  type EdgeZoneEnteredEvent,
} from './painted/PaintedScene';
import {
  loadJourneyManifest,
  SceneRouter,
  type SceneTransitionRunner,
  type SceneTransitionRequest,
} from './painted/SceneRouter';
import { ZonePreloader, type ZoneTextureLoader } from './painted/ZonePreloader';
import { PaintedSceneTransitionRunner } from './painted/SceneTransition';
import { ZoneMessageBus } from './painted/ZoneMessageBus';
import { ConnectedPaintedScene, CONNECTED_SCENE_EVENTS, type LocalPositionPayload } from './painted/ConnectedPaintedScene';
import { JourneyManifestSchema, type JourneyManifest } from './painted/journeyManifest.schema';
import { emitHearthChatMessage, emitSlipShow, emitVitalsChanged } from '../game/HudState';
import { isC5UiEnabled } from '../ui/c5/c5Flag';
import { FestivalToast } from '../ui/FestivalToast';
import { WhisperToast, whisperToastMessage } from '../ui/WhisperToast';
import { CraneFoldRitualUI } from '../ui/CraneFoldRitualUI';
import { PaperCraneSystem } from '../systems/PaperCraneSystem';
import { clampNormalized } from './painted/coordinateAdapter';
import { type JournalSceneDeps } from './JournalScene';
import { dispatchJournalSnapshot } from '../journal/journalDispatch';

export const PAINTED_WORLD_COORDINATOR_KEY = 'PaintedWorldCoordinator' as const;

export interface PaintedWorldCoordinatorInitData {
  characterName?: string;
  vocation?: string;
  realm?: string;
  slotIndex?: number;
}

const POSITION_PUSH_INTERVAL_MS = 100;

// ── Game events emitted by the coordinator for portal delegates ──

export const COORDINATOR_EVENTS = {
  DUNGEON_GATE_ENTER: 'coordinator:dungeon-gate-enter',
  HOME_ZONE_ENTER: 'coordinator:home-zone-enter',
  TOWN_ZONE_ENTER: 'coordinator:town-zone-enter',
  TOWN_SCREEN_CHANGED: 'coordinator:town-screen-changed',
} as const;

// ── Network transition runner ──

class NetworkSceneTransitionRunner implements SceneTransitionRunner {
  private readonly visualRunner: PaintedSceneTransitionRunner;

  constructor(
    private readonly room: () => Room<OverworldRoomState> | null,
    visualRunner: PaintedSceneTransitionRunner,
  ) {
    this.visualRunner = visualRunner;
  }

  async run(req: SceneTransitionRequest): Promise<void> {
    const room = this.room();
    if (!room) {
      await this.visualRunner.run(req);
      return;
    }
    await this.visualRunner.run(req);
  }
}

// ── Coordinator scene ──

export class PaintedWorldCoordinator extends Phaser.Scene {
  public static readonly KEY = PAINTED_WORLD_COORDINATOR_KEY;

  private initData: PaintedWorldCoordinatorInitData = {};
  private colyseusClient: Client | null = null;
  private room: Room<OverworldRoomState> | null = null;
  private localSessionId: string | null = null;

  private manifest: JourneyManifest | null = null;
  private router: SceneRouter | null = null;
  private preloader: ZonePreloader | null = null;
  private visualRunner: PaintedSceneTransitionRunner | null = null;

  private messageBus: ZoneMessageBus = new ZoneMessageBus();

  private activePaintedKey: string | null = null;
  private manifestToPhaser = new Map<string, string>();

  private lastPositionPushAt = 0;
  private pendingTransition: { destinationZoneId: string; edgeId: string } | null = null;

  // System-level UI widgets wired to Colyseus messages
  private festivalToast: FestivalToast | null = null;
  private whisperToast: WhisperToast | null = null;
  private craneFoldUI: CraneFoldRitualUI | null = null;
  private paperCraneSystem: PaperCraneSystem | null = null;

  constructor() {
    super({ key: PAINTED_WORLD_COORDINATOR_KEY });
  }

  sendRoomMessage(type: string, payload?: Record<string, unknown>): void {
    this.room?.send(type, payload);
  }

  leaveRoom(): void {
    if (this.room) {
      try { this.room.leave(); } catch { /* already left */ }
      this.room = null;
    }
  }

  init(data?: PaintedWorldCoordinatorInitData): void {
    this.initData = data ?? {};
    this.localSessionId = null;
    this.room = null;
    this.colyseusClient = null;
    this.manifest = null;
    this.router = null;
    this.preloader = null;
    this.visualRunner = null;
    this.activePaintedKey = null;
    this.lastPositionPushAt = 0;
    this.pendingTransition = null;
    this.messageBus = new ZoneMessageBus();
    this.festivalToast = null;
    this.whisperToast = null;
    this.craneFoldUI = null;
    this.paperCraneSystem = null;
    this.manifestToPhaser = this.collectManifestToPhaserMap();
  }

  preload(): void {
    if (!this.cache.json.has('journey-manifest')) {
      this.load.json('journey-manifest', 'assets/scenes/journey-manifest.json');
    }
  }

  create(): void {
    this.game.registry.set(PAINTED_SHELL_MANAGED_REGISTRY_KEY, true);
    this.bootRouter();
    this.bindGlobalEvents();
    this.connectToOverworld();
  }

  update(_time: number, _delta: number): void {
    this.maybePushLocalPosition();
  }

  // ── Boot helpers ──────────────────────────────────────────────────────

  private bootRouter(): void {
    const raw = this.cache.json.get('journey-manifest');
    if (!raw) {
      console.error('[PaintedWorldCoordinator] journey-manifest not in cache');
      return;
    }
    let manifest: JourneyManifest;
    try {
      manifest = loadJourneyManifestFromValue(raw);
    } catch (err) {
      console.error('[PaintedWorldCoordinator] manifest invalid:', err);
      return;
    }
    this.manifest = manifest;

    const loader: ZoneTextureLoader = {
      isTextureLoaded: (sceneKey: string) => {
        const phaserKey = this.manifestToPhaser.get(sceneKey);
        if (!phaserKey) return false;
        return Boolean(this.scene.get(phaserKey));
      },
      loadZoneTexture: async (sceneKey: string) => {
        const phaserKey = this.manifestToPhaser.get(sceneKey);
        if (!phaserKey) return;
        const spec = manifest.scenes.find((s) => s.key === sceneKey);
        if (!spec) return;
        const cacheKey = `spec:${sceneKey}`;
        if (this.cache.json.has(cacheKey)) return;
        await new Promise<void>((resolve) => {
          this.load.json(cacheKey, spec.assetPath);
          this.load.once('complete', () => resolve());
          this.load.once('loaderror', () => resolve());
          this.load.start();
        });
      },
      unloadZoneTexture: (sceneKey: string) => {
        const cacheKey = `spec:${sceneKey}`;
        if (this.cache.json.has(cacheKey)) {
          this.cache.json.remove(cacheKey);
        }
      },
    };

    this.preloader = new ZonePreloader(manifest, loader);
    this.visualRunner = new PaintedSceneTransitionRunner(this.game, {
      manifestToPhaserKey: this.manifestToPhaser,
    });
    const networkRunner = new NetworkSceneTransitionRunner(
      () => this.room,
      this.visualRunner,
    );

    const slotIndex = this.initData.slotIndex ?? 0;
    this.router = new SceneRouter({
      manifest,
      transition: networkRunner,
      preloader: this.preloader,
      storageKey: `painted_scene_state_${slotIndex}`,
    });

    const initialKey = this.router.currentSceneKey();
    this.launchPaintedScene(initialKey);
  }

  private collectManifestToPhaserMap(): Map<string, string> {
    const map = new Map<string, string>();
    if (!this.scene.manager) return map;
    for (const s of this.scene.manager.scenes) {
      if (s instanceof PaintedScene) {
        map.set(s.manifestKey, s.scene.key);
      }
    }
    return map;
  }

  private launchPaintedScene(manifestKey: string, spawn?: { x: number; y: number }): void {
    const phaserKey = this.manifestToPhaser.get(manifestKey);
    if (!phaserKey) {
      console.warn(`[PaintedWorldCoordinator] no Phaser scene for "${manifestKey}"`);
      return;
    }
    if (this.activePaintedKey === phaserKey) return;
    if (this.activePaintedKey) {
      this.scene.stop(this.activePaintedKey);
    }
    this.scene.launch(phaserKey, {
      ...(spawn ? { spawn } : {}),
      messageBus: this.messageBus,
      roomSend: (type: string, data?: unknown) => this.room?.send(type, data),
    });
    this.activePaintedKey = phaserKey;
  }

  private bindGlobalEvents(): void {
    this.game.events.on(
      PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED,
      this.onEdgeZoneEntered,
      this,
    );
    this.game.events.on(
      PAINTED_SCENE_EVENTS.READY,
      this.onPaintedSceneReady,
      this,
    );
    this.game.events.on(
      CONNECTED_SCENE_EVENTS.LOCAL_POSITION_UPDATE,
      this.onLocalPositionUpdate,
      this,
    );
    this.game.events.on(
      PAINTED_SCENE_EVENTS.JOURNAL_REQUESTED,
      this.onJournalRequested,
      this,
    );
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdown());
  }

  private onJournalRequested = (): void => {
    if (this.scene.isActive(JOURNAL_SCENE_KEY)) {
      this.scene.stop(JOURNAL_SCENE_KEY);
      if (this.activePaintedKey) this.scene.resume(this.activePaintedKey);
      return;
    }
    if (this.activePaintedKey) this.scene.pause(this.activePaintedKey);
    const deps: JournalSceneDeps = {
      sendMessage: (type, payload) => this.room?.send(type, payload),
      dismiss: () => {
        this.scene.stop(JOURNAL_SCENE_KEY);
        if (this.activePaintedKey) this.scene.resume(this.activePaintedKey);
      },
    };
    this.scene.launch(JOURNAL_SCENE_KEY, deps);
  };

  private onPaintedSceneReady = (_payload: { sceneKey: string }): void => {
    // ConnectedPaintedScene subscribes to the bus on ready;
    // nothing extra needed since we pass the bus via init data.
  };

  private onLocalPositionUpdate = (payload: LocalPositionPayload): void => {
    if (!this.room) return;
    const now = this.game.getTime();
    if (now - this.lastPositionPushAt < POSITION_PUSH_INTERVAL_MS) return;
    const clamped = clampNormalized(payload.localX, payload.localY);
    const msg: LocalPositionUpdateEvent = {
      v: COMBAT_PROTOCOL_VERSION,
      zoneId: payload.zoneId,
      localX: clamped.nx,
      localY: clamped.ny,
    };
    this.room.send(MSG_LOCAL_POSITION_UPDATE, msg);
    this.lastPositionPushAt = now;
  };

  private onEdgeZoneEntered = (payload: EdgeZoneEnteredEvent): void => {
    if (!this.router) return;
    if (this.pendingTransition) return;
    if (!this.room) {
      void this.router.goToViaEdgeZone(payload.zone.id).catch(() => {});
      return;
    }
    this.pendingTransition = {
      destinationZoneId: payload.zone.targetScene,
      edgeId: payload.zone.id,
    };
    const req: ZoneTransitionRequest = {
      v: COMBAT_PROTOCOL_VERSION,
      edgeId: payload.zone.id,
      destinationZoneId: payload.zone.targetScene,
    };
    this.room.send(MSG_ZONE_TRANSITION_REQUEST, req);
  };

  // ── Colyseus connection ───────────────────────────────────────────────

  private async connectToOverworld(): Promise<void> {
    try {
      this.colyseusClient = new Client(COLYSEUS_URL);
      const charName = this.initData.characterName
        ?? (this.registry.get('character:name') as string | undefined)
        ?? `Player-${Math.floor(Math.random() * 1000)}`;
      const charVocation = this.initData.vocation
        ?? (this.registry.get('character:vocation') as string | undefined);
      const charRealm = this.initData.realm
        ?? (this.registry.get('character:realm') as string | undefined);

      const joinOptions: Record<string, string> = { name: charName };
      if (charVocation) joinOptions.vocation = charVocation;
      if (charRealm) joinOptions.realm = charRealm;
      const demoUser = getDemoUserJoinOptions();
      if (demoUser) Object.assign(joinOptions, demoUser);

      this.room = await this.colyseusClient.joinOrCreate('overworld_room', joinOptions);
      this.localSessionId = this.room.sessionId;
      this.setupRoomListeners();
      this.registry.set('overworld:ready', true);
    } catch (err) {
      console.error('[PaintedWorldCoordinator] Failed to connect:', err);
      this.time.delayedCall(3000, () => {
        void this.connectToOverworld();
      });
    }
  }

  // ── Room message wiring ───────────────────────────────────────────────

  private setupRoomListeners(): void {
    if (!this.room) return;

    // ── Zone-relevant messages → ZoneMessageBus ──
    this.wireZoneMessages();

    // ── Zone navigation messages (handled by coordinator) ──
    this.wireZoneNavigation();

    // ── System-level messages (handled by coordinator) ──
    this.wireSystemMessages();

    // ── Schema-based player tracking → bus ──
    this.wirePlayerSchema();
  }

  private wireZoneMessages(): void {
    if (!this.room) return;
    const bus = this.messageBus;

    // Player position — the server broadcasts world-space x/y via
    // MSG_PLAYER_POSITION, but for painted zones the schema's localX/localY
    // (wired in wirePlayerSchema) is the authoritative source. We still
    // forward the raw event so ConnectedPaintedScene can reconcile if needed.
    this.room.onMessage(MSG_PLAYER_POSITION, (ev: PlayerPositionEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      if (ev.sessionId === this.localSessionId) return;
      bus.publish(MSG_PLAYER_POSITION, ev);
    });

    this.room.onMessage(MSG_ENTITY_SPAWN, (ev: EntitySpawnEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      bus.publish(MSG_ENTITY_SPAWN, ev);
    });
    this.room.onMessage(MSG_ENTITY_DESPAWN, (ev: EntityDespawnEvent) => {
      bus.publish(MSG_ENTITY_DESPAWN, ev);
    });
    this.room.onMessage(MSG_NPC_SPAWN, (ev: NpcSpawnEvent) => {
      bus.publish(MSG_NPC_SPAWN, ev);
    });
    this.room.onMessage(MSG_NPC_DIALOGUE, (ev: NpcDialogueEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      bus.publish(MSG_NPC_DIALOGUE, ev);
    });
    this.room.onMessage(MSG_GATHER_NODE_SPAWN, (ev: GatherNodeSpawnEvent) => {
      bus.publish(MSG_GATHER_NODE_SPAWN, ev);
    });
    this.room.onMessage(MSG_GATHER_NODE_COOLDOWN, (ev: GatherNodeCooldownEvent) => {
      bus.publish(MSG_GATHER_NODE_COOLDOWN, ev);
    });
    this.room.onMessage(MSG_GATHER_COMPLETE, (ev: GatherCompleteResult) => {
      bus.publish(MSG_GATHER_COMPLETE, ev);
    });
    this.room.onMessage(MSG_GATHER_CANCELLED, (ev: GatherCancelledEvent) => {
      bus.publish(MSG_GATHER_CANCELLED, ev);
    });
    this.room.onMessage(MSG_ENEMY_WINDUP, (ev: EnemyWindupEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      bus.publish(MSG_ENEMY_WINDUP, ev);
    });
    this.room.onMessage(MSG_PLAYER_FAINT, (ev: PlayerFaintEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      bus.publish(MSG_PLAYER_FAINT, ev);
      // Launch the faint cinematic overlay for the local player.
      if (ev.sessionId === this.localSessionId) {
        this.launchFaintOverlay(ev);
      }
    });
    this.room.onMessage(MSG_WEATHER_CHANGE, (ev: WeatherChangePayload) => {
      bus.publish(MSG_WEATHER_CHANGE, ev);
    });
    this.room.onMessage(MSG_SKY_STATE, (ev: SkyStatePayload) => {
      bus.publish(MSG_SKY_STATE, ev);
    });
    this.room.onMessage(MSG_PLAYER_REALM_CHANGE, (ev: PlayerRealmChangeEvent) => {
      bus.publish(MSG_PLAYER_REALM_CHANGE, ev);
    });
    this.room.onMessage(MSG_REALM_FIRST_VISIT, (ev: RealmFirstVisitEvent) => {
      bus.publish(MSG_REALM_FIRST_VISIT, ev);
    });
    this.room.onMessage(MSG_FIRE_SAT, (ev: FireSatEvent) => {
      bus.publish(MSG_FIRE_SAT, ev);
    });
    this.room.onMessage(MSG_FIRE_STOOD, (ev: FireStoodEvent) => {
      bus.publish(MSG_FIRE_STOOD, ev);
    });
    this.room.onMessage(MSG_MARSHMALLOW_ROAST_TICK, (ev: MarshmallowRoastTickEvent) => {
      bus.publish(MSG_MARSHMALLOW_ROAST_TICK, ev);
    });
    this.room.onMessage(MSG_MARSHMALLOW_ROAST_COMPLETE, (ev: MarshmallowRoastCompleteEvent) => {
      bus.publish(MSG_MARSHMALLOW_ROAST_COMPLETE, ev);
    });
    this.room.onMessage(MSG_FIRE_EMOTE_PLAYED, (ev: FireEmotePlayedEvent) => {
      bus.publish(MSG_FIRE_EMOTE_PLAYED, ev);
    });
    this.room.onMessage(MSG_AUTO_TRAVEL_STATE, (ev: AutoTravelStateEvent) => {
      bus.publish(MSG_AUTO_TRAVEL_STATE, ev);
    });
    this.room.onMessage(MSG_AUTO_TRAVEL_DESTINATIONS, (ev: AutoTravelDestinationsEvent) => {
      bus.publish(MSG_AUTO_TRAVEL_DESTINATIONS, ev);
    });
  }

  private wireZoneNavigation(): void {
    if (!this.room) return;

    this.room.onMessage(MSG_ZONE_ENTER, (msg: ZoneEnterEvent) => {
      this.messageBus.publish(MSG_ZONE_ENTER, msg);
      if (msg.sessionId === this.localSessionId) {
        this.handleLocalZoneEnter(msg);
      }
    });
    this.room.onMessage(MSG_ZONE_LEAVE, (msg: ZoneLeaveEvent) => {
      this.messageBus.publish(MSG_ZONE_LEAVE, msg);
    });
    this.room.onMessage(MSG_ZONE_GRAPH, (msg: ZoneGraphPayload) => {
      this.messageBus.publish(MSG_ZONE_GRAPH, msg);
      if (this.preloader) {
        this.preloader.setConnectionGraph(msg.edges);
      }
    });
  }

  private wireSystemMessages(): void {
    if (!this.room) return;

    this.room.onMessage(MSG_DUNGEON_GATE_ENTER, () => {
      this.game.events.emit(COORDINATOR_EVENTS.DUNGEON_GATE_ENTER);
    });
    this.room.onMessage(MSG_HOME_ZONE_ENTER, (ev: HomeZoneEnterEvent) => {
      this.game.events.emit(COORDINATOR_EVENTS.HOME_ZONE_ENTER, ev);
    });
    this.room.onMessage(MSG_TOWN_ZONE_ENTER, (ev: TownZoneEnterEvent) => {
      this.game.events.emit(COORDINATOR_EVENTS.TOWN_ZONE_ENTER, ev);
    });
    this.room.onMessage(MSG_TOWN_SCREEN_CHANGED, (ev: TownScreenChangedEvent) => {
      this.game.events.emit(COORDINATOR_EVENTS.TOWN_SCREEN_CHANGED, ev);
    });

    this.room.onMessage(MSG_HEARTH_WHISPER_BROADCAST, (ev: HearthWhisperBroadcastEvent) => {
      emitHearthChatMessage(this.game, ev);
    });

    this.room.onMessage(MSG_RETURN_DIGEST, (payload: ReturnDigestPayload) => {
      const entries = (this.game.registry.get('festival.journalEntries') as Array<{ id: string; heading: string; body: string; timestamp: number }>) ?? [];
      entries.push({
        id: 'return-digest-waking',
        heading: 'Return',
        body: 'You were away. The world continued.',
        timestamp: Date.now(),
      });
      this.game.registry.set('festival.journalEntries', entries);
    });

    this.room.onMessage(MSG_PLAYER_ATTACK_HIT, (ev: PlayerAttackHitEvent) => {
      if (ev.v !== COMBAT_PROTOCOL_VERSION) return;
      this.game.events.emit('coordinator:attack-hit', ev);
    });

    this.room.onMessage(MSG_EXPEDITION_STATE, (ev: ExpeditionStateBroadcast) => {
      this.game.events.emit('coordinator:expedition-state', ev);
    });
    this.room.onMessage(MSG_EXPEDITION_REWARD_GRANTED, (ev: ExpeditionRewardGrantedEvent) => {
      this.game.events.emit('coordinator:expedition-reward', ev);
    });

    this.room.onMessage(MSG_PET_SNAPSHOT, (ev: PetSnapshotEvent) => {
      this.game.events.emit('coordinator:pet-snapshot', ev);
    });

    this.room.onMessage(MSG_CRANE_CROSSING, (ev: CraneCrossingEvent) => {
      this.game.events.emit('coordinator:crane-crossing', ev);
    });
    this.room.onMessage(MSG_CRANE_DEPARTED, (ev: CraneDepartedEvent) => {
      this.game.events.emit('coordinator:crane-departed', ev);
    });

    this.room.onMessage(MSG_WAYSTONE_INFO, (ev: WaystoneInfoPayload) => {
      this.game.events.emit('coordinator:waystone-info', ev);
    });

    this.room.onMessage(MSG_JOURNAL_SNAPSHOT, (payload: unknown) => {
      const snapshot = dispatchJournalSnapshot(payload);
      if (snapshot) this.game.events.emit('journal:snapshot', snapshot);
    });

    this.room.onMessage('__npc_debug__', (ev: { ok: boolean; reason: string; detail?: unknown }) => {
      const tag = ev.ok ? '[NPC-DIAG] server OK' : '[NPC-DIAG] server REJECTED';
      console.warn(tag, ev.reason, ev.detail ?? '');
    });

    // Sub-systems that self-register their own Colyseus listeners
    this.festivalToast = new FestivalToast(this, null, this.room!);
    if (isC5UiEnabled()) {
      // C5: new information arrives as paper. The whisper line becomes a
      // loose slip on the painting's corner instead of a Phaser text object.
      this.room.onMessage(MSG_JOURNAL_WHISPER_GRANTED, (raw: unknown) => {
        const payload = raw as WhisperGrantedPayload;
        if (payload.whisperIds.length === 0) return;
        emitSlipShow(this.game, whisperToastMessage(payload.whisperIds.length));
      });
    } else {
      this.whisperToast = new WhisperToast(this, this.room!);
    }
    this.craneFoldUI = new CraneFoldRitualUI(this, this.room!);
    this.paperCraneSystem = new PaperCraneSystem(this, this.room!);
  }

  private wirePlayerSchema(): void {
    if (!this.room) return;
    const bus = this.messageBus;

    this.room.state.players.onAdd((player, sessionId) => {
      if (sessionId === this.localSessionId) {
        // The local player's schema is the only source of real vitals in the
        // client; publish it for the C5 ink-card (and any future HUD).
        this.publishLocalVitals(player);
        player.onChange(() => this.publishLocalVitals(player));
        return;
      }
      this.publishSchemaPosition(sessionId, player);
      player.onChange(() => this.publishSchemaPosition(sessionId, player));
    });

    this.room.state.players.onRemove((_player, sessionId) => {
      bus.publish(MSG_ENTITY_DESPAWN, {
        v: COMBAT_PROTOCOL_VERSION,
        entityId: sessionId,
        reason: 'disconnected',
      });
    });
  }

  /** Mirror the local player's vitals onto the HudState bus (C5 Layer 0). */
  private publishLocalVitals(player: {
    health?: number;
    maxHealth?: number;
    mana?: number;
    maxMana?: number;
  }): void {
    const maxHealth = player.maxHealth ?? 0;
    const maxMana = player.maxMana ?? 0;
    if (maxHealth <= 0 && maxMana <= 0) return;
    emitVitalsChanged(this.game, {
      health: player.health ?? 0,
      maxHealth,
      mana: player.mana ?? 0,
      maxMana,
    });
  }

  private publishSchemaPosition(
    sessionId: string,
    player: { name: string; sceneZoneId: string; localX: number; localY: number; attendanceState?: string },
  ): void {
    const clamped = clampNormalized(player.localX, player.localY);
    this.messageBus.publish(MSG_PLAYER_POSITION, {
      v: COMBAT_PROTOCOL_VERSION,
      sessionId,
      x: clamped.nx,
      y: clamped.ny,
      attendanceState: player.attendanceState,
    });
  }

  // ── Zone transition handling ──────────────────────────────────────────

  private handleLocalZoneEnter(msg: ZoneEnterEvent): void {
    if (!this.router) return;
    const pending = this.pendingTransition;
    this.pendingTransition = null;

    void this.router
      .goTo(msg.zoneId, {
        ...(pending?.edgeId ? { viaEdgeZoneId: pending.edgeId } : {}),
        spawn: { x: msg.localX, y: msg.localY },
      })
      .then(() => {
        const phaserKey = this.manifestToPhaser.get(msg.zoneId);
        if (phaserKey) {
          this.activePaintedKey = phaserKey;
          this.passBusToActiveScene();
        }
      })
      .catch((err) => {
        console.warn('[PaintedWorldCoordinator] router.goTo failed:', err);
      });
  }

  private passBusToActiveScene(): void {
    if (!this.activePaintedKey) return;
    const scene = this.scene.get(this.activePaintedKey);
    if (scene instanceof ConnectedPaintedScene) {
      scene.setMessageBus(this.messageBus);
    }
  }

  // ── Outbound position throttle ────────────────────────────────────────

  private maybePushLocalPosition(): void {
    // Position pushing is handled reactively via onLocalPositionUpdate,
    // triggered by ConnectedPaintedScene emitting LOCAL_POSITION_UPDATE.
    // This update() hook is reserved for future per-frame work.
  }

  // ── Faint cinematic ────────────────────────────────────────────────────

  private launchFaintOverlay(ev: PlayerFaintEvent): void {
    // Don't double-launch if already running.
    if (this.scene.isActive(FAINT_OVERLAY_SCENE_KEY)) return;
    this.scene.launch(FAINT_OVERLAY_SCENE_KEY, { faintEvent: ev });
  }

  // ── Teardown ──────────────────────────────────────────────────────────

  private shutdown(): void {
    this.game.events.off(
      PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED,
      this.onEdgeZoneEntered,
      this,
    );
    this.game.events.off(
      PAINTED_SCENE_EVENTS.READY,
      this.onPaintedSceneReady,
      this,
    );
    this.game.events.off(
      CONNECTED_SCENE_EVENTS.LOCAL_POSITION_UPDATE,
      this.onLocalPositionUpdate,
      this,
    );
    this.game.events.off(
      PAINTED_SCENE_EVENTS.JOURNAL_REQUESTED,
      this.onJournalRequested,
      this,
    );
    this.messageBus.clear();
    if (this.room) {
      try { this.room.leave(); } catch { /* already left */ }
      this.room = null;
    }
    this.colyseusClient = null;
    this.game.registry.set(PAINTED_SHELL_MANAGED_REGISTRY_KEY, false);
  }
}

function loadJourneyManifestFromValue(raw: unknown): JourneyManifest {
  return JourneyManifestSchema.parse(raw);
}

export { loadJourneyManifest };
